/**
 * Pool-only customer lifecycle (Slice 16). Non-pool tenants: no route filtering by lifecycle.
 */

import { supabase } from '../../supabase';
import { foundation } from '../../ui/tokens';
import { getStatusTone } from '../../ui/statusTone';
import { invalidatePoolPeriodCachesForCustomer } from '../ui/prefetchPoolPeriodBundles';
import { invalidateLogJobCustomerCache } from '../../hooks/useLogJobCustomerPrefetch';
import { canAccessFeature } from '../ui/featureAccess';
import { getCurrentTenantRole } from '../auth/currentTenantRoleStore';
import { withTimeout } from '../async/withTimeout';
import { isOneOffCustomer } from './expectedVisit';

import { poolLifecycleTone } from './poolLifecycleTone';

export { POOL_LIFECYCLE_VALUES, poolLifecycleTone } from './poolLifecycleTone';

/**
 * Worker / supervisor / admin: narrow lifecycle update (RLS-safe RPC).
 *
 * Calls `set_customer_lifecycle_status_v2` (2026-09-18 contract) — a superset
 * of the old `set_customer_lifecycle_status`: same params, plus
 * `p_include_job_ids` and a richer return shape
 * `{staged, invoice_id, invoice_number, invoice_number_year, total,
 *   included_job_ids, skip_reason, left_out:{count,total|null}}` instead of
 * just `{error}`. Every existing caller that passes no 4th arg keeps working
 * unchanged — `p_include_job_ids` stays null, which the RPC treats as
 * "default eligibility" (the old v1 behavior).
 *
 * @param {string} customerId
 * @param {string} lifecycleStatus
 * @param {string|undefined} serviceStartDate
 * @param {{ includeJobIds?: string[], excludeJobIds?: string[], role?: string }} [options]
 *   `includeJobIds` — ticked "other" job ids from the transition sheet.
 *   `excludeJobIds` — priced weekly / extra / off-season visits the office
 *   unticked so they stay in Ready to bill. Empty means the one invoice.
 *   `role` — the CALLER's role; ids are stripped (never sent) unless the role
 *   can access the `invoices` feature (admin/billing) — a field role's ticks
 *   (which the sheet never even collects — worker/supervisor render
 *   counts-only) must never reach the RPC. Defense in depth: the sheet is the
 *   first gate, this is the second ("two doors" pattern).
 * @returns {Promise<{ error: Error|null, outcome: object|null }>}
 *   `outcome` is the RPC's return payload on success, null on error/no-op.
 */
export async function setCustomerLifecycleStatus(customerId, lifecycleStatus, serviceStartDate, options = {}) {
  const { includeJobIds, excludeJobIds, role: optionsRole } = options;
  const role = optionsRole ?? getCurrentTenantRole();
  const params = {
    p_customer_id: customerId,
    p_lifecycle_status: lifecycleStatus,
  };
  if (serviceStartDate) params.p_service_start_date = serviceStartDate;
  if (Array.isArray(includeJobIds) && includeJobIds.length > 0 && canAccessFeature(role, 'invoices')) {
    params.p_include_job_ids = includeJobIds;
  }
  if (Array.isArray(excludeJobIds) && excludeJobIds.length > 0 && canAccessFeature(role, 'invoices')) {
    params.p_exclude_job_ids = excludeJobIds;
  }
  const { data, error } = await withTimeout(
    Promise.resolve(supabase.rpc('set_customer_lifecycle_status_v2', params)),
  );
  // A lifecycle change moves the customer's opening/closing window_start. The
  // route board's period caches key the bundle/window by (tenant, context,
  // customer) and otherwise persist until SIGNED_OUT — so evict this customer's
  // entries here, or the next board paint shows a stale-window over-count that
  // flips to the right number ("4 → 0" on re-open). Only on a successful write.
  if (!error) {
    invalidatePoolPeriodCachesForCustomer(customerId);
    invalidateLogJobCustomerCache(customerId);
  }
  const outcome = (!error && data) ? (Array.isArray(data) ? data[0] : data) : null;
  return { error, outcome };
}

/**
 * Stage-only retry (KAN-1031): the status already changed, only the draft is missing. Admin-only
 * RPC; returns the draft's invoice id, or null when nothing was staged.
 * @returns {Promise<{ error: any, invoiceId: string|null }>}
 */
export async function stageLifecycleInvoiceNow(customerId, context) {
  const { data, error } = await withTimeout(
    Promise.resolve(supabase.rpc('stage_lifecycle_invoice_now', { p_customer_id: customerId, p_context: context })),
  );
  if (!error) invalidatePoolPeriodCachesForCustomer(customerId);
  return { error, invoiceId: !error && data ? String(data) : null };
}

/** Route board includes only these pool customers */
export function isPoolIndustryForLifecycle(industryType) {
  return industryType === 'pool';
}

/** Eligible for the recurring route stop when the tenant is a pool company.
 *  Pass `dateStr` (YYYY-MM-DD) to gate on service_start_date / service_stop_date
 *  — the weekly stop stays off the board outside that window. Logged extras
 *  must NOT pass a date: customerAllowedOnStops calls this without one so a
 *  visit already logged still appears on its own day. */
export function customerEligibleForPoolRouteBoard(customer, dateStr) {
  // Archived customers are hidden from every active surface — never on the board.
  if (customer?.archived_at) return false;
  // A one-off client has NO pool season: `lifecycle_status` is NULL by the
  // customers_one_off_has_no_lifecycle CHECK (20260918001400). The `?? 'active'`
  // below would therefore resolve a one-off to ACTIVE and put it on the
  // recurring route board. Today that is masked only because computeExpectedVisit
  // bails on a null `service_day` — but the migration's backfill nulls only
  // lifecycle_status/service_start_date, so a LEGACY one-off created back when
  // one-off intake reused the recurring form can still carry a service_day and
  // would become a recurring stop on every role's board.
  //
  // This guard lives HERE, not at a call site: there are six callers (this file's
  // consumers in useAdminDerivedData, useWorker/SupervisorDashboardState,
  // useWorker/SupervisorDerivedData, and customerAllowedOnStops) and guarding one
  // of them is exactly the "fixed the function, missed the action" failure mode.
  if (isOneOffCustomer(customer)) return false;
  const s = customer?.lifecycle_status ?? 'active';
  if (s !== 'active') return false;
  if (dateStr && customer?.service_start_date) {
    if (dateStr < customer.service_start_date) return false;
  }
  if (dateStr && customer?.service_stop_date && dateStr >= customer.service_stop_date) return false;
  return true;
}

/** @deprecated Prefer poolLifecycleTone + DotText. Kept for tests that read palette keys. */
export const LIFECYCLE_BADGE_PALETTE = {
  opening: { color: getStatusTone('info').fg },
  active: { color: getStatusTone('success').fg },
  closing: { color: getStatusTone('info').fg },
  closed: { color: getStatusTone('neutral').fg },
  inactive: { color: getStatusTone('neutral').fg },
};

/** Label for Admin badges (Opening, Active, …). Accepts optional intl. */
export function poolLifecycleBadgeLabel(lifecycleStatus, intl) {
  const s = lifecycleStatus ?? 'active';
  if (intl) {
    const map = {
      active: intl.formatMessage({ id: 'customers.lifecycleActive', defaultMessage: 'Active' }),
      inactive: intl.formatMessage({ id: 'customers.lifecycleInactive', defaultMessage: 'Inactive' }),
      opening: intl.formatMessage({ id: 'customers.lifecycleOpening', defaultMessage: 'Opening' }),
      closing: intl.formatMessage({ id: 'customers.lifecycleClosing', defaultMessage: 'Closing' }),
      closed: intl.formatMessage({ id: 'customers.lifecycleClosed', defaultMessage: 'Closed' }),
    };
    return map[s] || s.charAt(0).toUpperCase() + s.slice(1);
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// poolTypeDisplayLabel moved to ./poolTypeLabel.js — it's a pure display helper
// and this module pulls in the Supabase client, which a presentational component
// (and its tests) must not have to load just to translate a label.

/** Dot/text row style for leftover span call sites. Prefer DotText + poolLifecycleTone. */
export function poolLifecycleBadgeStyle(lifecycleStatus) {
  const tone = getStatusTone(poolLifecycleTone(lifecycleStatus));
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    fontSize: foundation.fontSize.xs,
    fontWeight: foundation.fontWeight.semibold,
    lineHeight: 1.2,
    color: tone.fg,
    background: 'transparent',
    border: 'none',
    padding: 0,
  };
}
